//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { RIGHTS_STATUS } from '@apis/rights-management/rightsManagementApiModels';
import { RequiredPermissionsButton } from '@components/atoms/required-permissions-button/RequiredPermissionsButton';
import { CaptionSecondaryText } from '@components/atoms/text/Text';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import { IRightsOption } from '@legacy-components/rights-management/request-rights-model';
import Cuid from '@models/Cuid';
import {
  UIATrackingIds,
  UIA_RIGHTS_REQUEST_PERMISSION,
} from '@uia/constants/uiaConstants';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { useRightsConfigurationsQuery } from '@uia/rights/hooks/useRightsConfigurationsQuery';
import { getRightsRequestDetailsWithSyndicationCheck } from '@uia/rights/utils/rightsRequestUtils';
import { isDefined } from '@utils/filterPredicates';

export interface RequestRequiredForMissingTCUrlProps {
  cuid: Cuid;
  isReRequestRequiredForMissingTCUrl: boolean;
  requestType: RightsRequestOptions;
  setRightsRequestText: (value: string) => void;
  setSelectedOption: (selectedOption: IRightsOption) => void;
}

export const RequestRequiredForMissingTCUrl = ({
  cuid,
  isReRequestRequiredForMissingTCUrl,
  requestType,
  setRightsRequestText: _setRightsRequestText,
  setSelectedOption,
}: RequestRequiredForMissingTCUrlProps) => {
  const {
    data: metadata,
    isError: isErrorMetadata,
    isPending: isPendingMetadata,
  } = useMediaMetadataQuery(cuid);

  const {
    data: rightsOptions,
    isError: isErrorRightsConfigs,
    isPending: isPendingRightsConfigs,
  } = useRightsConfigurationsQuery(Cuid.toPartial(cuid), requestType);

  if (
    isPendingMetadata ||
    isErrorMetadata ||
    isPendingRightsConfigs ||
    isErrorRightsConfigs
  ) {
    return null;
  }

  const { status } = getRightsRequestDetailsWithSyndicationCheck(metadata);

  return (
    <>
      {status !== RIGHTS_STATUS.EXPIRED &&
        isReRequestRequiredForMissingTCUrl && (
          <FlexStart>
            <RequiredPermissionsButton
              onClick={() => {
                const defaultOption = rightsOptions.find(
                  (item) => item.isDefault,
                );
                if (isDefined(defaultOption)) {
                  setSelectedOption(defaultOption);
                }
              }}
              permissionName={UIA_RIGHTS_REQUEST_PERMISSION}
              trackingId={UIATrackingIds.RIGHTS_REQUEST_REQUEST_AGAIN_BUTTON}
            >
              Request Rights Again
            </RequiredPermissionsButton>
            <CaptionSecondaryText>
              Requests are considered Rejected if the owner hasn't responded
              within 3 weeks.
            </CaptionSecondaryText>
          </FlexStart>
        )}
    </>
  );
};

const FlexStart = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: start;
    gap: ${theme.spacings[2]};
  `,
);
